import { useState, type ChangeEventHandler, type MouseEventHandler } from 'react';
import { cn, useFieldContext } from '@bridebook/aura';
import { FakeInput } from '../fake-input';
import type { DateInputProps } from './types';
import { formatDisplayDate } from './utils';

/** TLDR: Native `<input type="date">` overlaid on a FakeInput — used on touch devices. */
export const DateInputMobile = ({
  id,
  value,
  onChange,
  onBlur,
  disabled: disabledProp,
  error: errorProp,
  placeholder,
  icon,
  className,
  classNames,
  min,
  max,
  locale,
  formatDate,
  dayPickerProps: _dayPickerProps,
  showClearBtn = true,
  ...fakeInputProps
}: DateInputProps) => {
  const context = useFieldContext();
  const disabled = disabledProp ?? context?.disabled ?? false;
  const error = errorProp ?? context?.error ?? false;
  const [focused, setFocused] = useState(false);
  const localeCode =
    locale && 'code' in locale && typeof locale.code === 'string' ? locale.code : undefined;

  const displayValue = formatDate ? formatDate(value) : formatDisplayDate(value, localeCode);

  const handleNativeChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    onChange(event.target.value);
    // Android doesn't fire blur when the native picker closes, so clear focus here.
    // On iOS the picker stays open after selection and blur fires on confirm — skip.
    if (/android/i.test(navigator.userAgent)) {
      setFocused(false);
    }
    onBlur?.();
  };

  const handleNativeClick: MouseEventHandler<HTMLInputElement> = (event) => {
    try {
      event.currentTarget.showPicker();
    } catch {
      // showPicker() can throw if already open or unsupported
    }
  };

  const handleNativeBlur = () => {
    setFocused(false);
    onBlur?.();
  };

  const handleClear = () => {
    onChange('');
    onBlur?.();
  };

  return (
    <div className="group relative">
      <FakeInput
        focused={focused}
        placeholder={placeholder}
        icon={icon}
        value={displayValue}
        error={error}
        disabled={disabled}
        showClearBtn={showClearBtn}
        onClear={handleClear}
        className={className}
        classNames={{
          ...classNames,
          clearButton: cn(classNames?.clearButton, 'z-20'),
        }}
        {...fakeInputProps}
      />

      <input
        id={id}
        type="date"
        value={value}
        onChange={handleNativeChange}
        onFocus={() => setFocused(true)}
        onBlur={handleNativeBlur}
        onClick={handleNativeClick}
        disabled={disabled}
        min={min}
        max={max}
        className="absolute inset-0 z-10 size-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
      />
    </div>
  );
};
